import React, { useState } from 'react';
import { UserPlus, Users, Check, Clock, CreditCard, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { orgService } from '../../services/orgService';

/**
 * Seat + roster management for the practice administrator.
 * Hybrid billing: if a free seat exists, invite fills it (no charge). If not,
 * inviting opens a confirm showing $15/mo + today's proration, charges the card
 * immediately, and sends the invite at the moment of charge.
 * Seat #1 is always the administrator (they get a full AI account too).
 */
const SeatManagement = ({ org, members, onChanged }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sentTo, setSentTo] = useState('');
  const [showSent, setShowSent] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const seatCount = org?.seatCount ?? 0;
  const seatsUsed = org?.seatsUsed ?? 0;
  const seatsLeft = Math.max(0, seatCount - seatsUsed);

  const submitInvite = async (addSeat) => {
    setBusy(true);
    const invited = email.trim();
    try {
      await orgService.invite(invited, name.trim(), addSeat);
      setEmail(''); setName(''); setShowConfirm(false); setPreview(null);
      setSentTo(invited); setShowSent(true); // advisory popup
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invite failed');
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (seatsLeft > 0) { submitInvite(false); return; }
    // No free seat — preview the prorated charge, then confirm
    setBusy(true);
    try {
      const p = await orgService.getSeatPreview();
      setPreview(p);
    } catch {
      setPreview({ pricePerSeat: 15, prorationAmount: null });
    } finally {
      setBusy(false);
      setShowConfirm(true);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await orgService.removeMember(removeTarget.email);
      toast.success(`Removed ${removeTarget.name || removeTarget.email} and cancelled their seat`);
      setRemoveTarget(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove clinician');
    } finally {
      setRemoving(false);
    }
  };

  const manageBilling = async () => {
    try {
      const { url } = await orgService.openPortal();
      if (url) window.open(url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Billing portal unavailable');
    }
  };

  const clinicians = (members || []).filter(m => m.role === 'clinician');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Seats & Clinicians</h3>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span>
            <span className="font-bold text-blue-600">{seatsUsed}</span>
            <span className="text-gray-500"> / {seatCount} seats used</span>
            {seatsLeft > 0 && <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{seatsLeft} free</span>}
          </span>
          {org?.stripeCustomerId && (
            <button onClick={manageBilling} className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800 text-xs">
              <CreditCard className="w-3.5 h-3.5" /> Manage billing
            </button>
          )}
        </div>
      </div>
      <p className="text-sm font-semibold text-gray-600 mb-4">Seat 1 is you (a full AI account). Each additional seat is $15/month.</p>

      <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="clinician@practice.com" required disabled={busy}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <input
          type="text" value={name} onChange={e => setName(e.target.value)}
          placeholder="Full name (optional)" disabled={busy}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="submit" disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg whitespace-nowrap"
        >
          <UserPlus className="w-4 h-4" /> {seatsLeft > 0 ? 'Invite' : 'Add seat & invite'}
        </button>
      </form>

      <div className="divide-y divide-gray-100">
        {clinicians.length === 0 && <p className="text-base font-medium text-gray-600 py-2">No clinicians yet — invite your first above.</p>}
        {clinicians.map(m => (
          <div key={m.email} className="flex items-center justify-between py-2">
            <div>
              <div className="text-sm font-medium text-gray-800">{m.name || m.email}</div>
              <div className="text-xs text-gray-500">{m.email}</div>
            </div>
            <div className="flex items-center gap-3">
              {m.status === 'active' ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><Check className="w-3 h-3" /> Active</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Invited</span>
              )}
              <button
                onClick={() => setRemoveTarget(m)}
                title="Remove clinician & cancel seat"
                className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Invitation-sent advisory popup */}
      {showSent && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Invitation sent to {sentTo}</h4>
            <p className="text-sm text-gray-600 mb-4">
              We've emailed them sign-in instructions. <strong>If they don't see it within a few minutes, ask them to check their spam or junk folder</strong> — new senders sometimes land there at first. They'll join your practice as soon as they sign in with that email address.
            </p>
            <button onClick={() => setShowSent(false)}
              className="px-5 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">Got it</button>
          </div>
        </div>
      )}

      {/* Remove clinician / cancel seat confirm */}
      {removeTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-2">Remove {removeTarget.name || removeTarget.email}?</h4>
            <p className="text-sm text-gray-600 mb-4">
              This <span className="font-semibold">cancels their seat</span> (your subscription drops by one seat — $15/month less, prorated credit on your next invoice) and removes their access. Their caseload analytics are <span className="font-semibold">retained by your practice</span> as a former-clinician record; their personal login and note templates remain theirs.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRemoveTarget(null)} disabled={removing}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={confirmRemove} disabled={removing}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-medium">
                {removing ? 'Removing…' : 'Remove & cancel seat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm-then-charge modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-2">Add a seat to invite {email}?</h4>
            <p className="text-sm text-gray-600 mb-4">
              You're out of free seats. Adding one seat is <span className="font-semibold">$15/month</span>
              {preview?.prorationAmount != null
                ? <> — and <span className="font-semibold">${preview.prorationAmount.toFixed(2)}</span> prorated for the rest of this billing period, charged now.</>
                : <>, prorated for the rest of this billing period and charged now.</>}
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowConfirm(false); setPreview(null); }} disabled={busy}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => submitInvite(true)} disabled={busy}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium">
                {busy ? 'Processing…' : 'Confirm & invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatManagement;
